import { useState, useEffect, useCallback, useRef } from "react";
import { useActivityDetection } from "./useActivityDetection";
import { articlesApi } from "../api/articles.api";

interface UseReadingTimerOptions {
  totalTime: number;
  articleId?: string;
  questId?: string;
  onComplete?: () => void;
  autoValidate?: boolean;
  inactivityThreshold?: number; // ms before pause
  tickInterval?: number; // ms per tick
}

interface UseReadingTimerReturn {
  timeElapsed: number;
  isPaused: boolean;
  isCompleted: boolean;
  progress: number;
  start: () => void;
  pause: () => void;
  resume: () => void;
  reset: () => void;
  resetActivity: () => void;
}

export const useReadingTimer = ({
  totalTime,
  articleId,
  questId,
  onComplete,
  autoValidate = true,
  inactivityThreshold = 15000,
  tickInterval = 1000,
}: UseReadingTimerOptions): UseReadingTimerReturn => {
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [isManuallyPaused, setIsManuallyPaused] = useState(false);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastCheckRef = useRef<number>(Date.now());

  // ✅ use refs to avoid stale closures inside setInterval
  const isRunningRef = useRef(isRunning);
  const isPausedRef = useRef(isPaused);
  const isCompletedRef = useRef(isCompleted);
  const timeElapsedRef = useRef(timeElapsed);
  const isManuallyPausedRef = useRef(isManuallyPaused);

  const syncRefs = useCallback(() => {
    isRunningRef.current = isRunning;
    isPausedRef.current = isPaused;
    isCompletedRef.current = isCompleted;
    timeElapsedRef.current = timeElapsed;
    isManuallyPausedRef.current = isManuallyPaused;
  }, [isRunning, isPaused, isCompleted, timeElapsed, isManuallyPaused]);

  const clearTimers = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = null;
  };

  const { getTimeSinceLastActivity } = useActivityDetection({
    onActivity: () => {
      // Reset the last check time when user is active
      lastCheckRef.current = Date.now();

      // Resume timer if it was paused due to inactivity
      if (
        isPausedRef.current &&
        isRunningRef.current &&
        !isCompletedRef.current
      ) {
        setIsPaused(false);
      }
    },
  });

  const handleCompletion = useCallback(async () => {
    setIsCompleted(true);
    setIsRunning(false);
    clearTimers();

    if (autoValidate && articleId) {
      try {
        await articlesApi.readed(articleId, questId);
        onComplete?.();
      } catch (error) {
        console.error("Failed to mark article as read:", error);
      }
    }
  }, [onComplete, autoValidate, articleId, questId]);

  const start = useCallback(() => {
    if (isCompletedRef.current) return;
    setIsRunning(true);
    setIsPaused(false);
    lastCheckRef.current = Date.now();
  }, []);

  const pause = useCallback(() => {
    setIsPaused(true);
  }, []);

  const resume = useCallback(() => {
    if (!isCompletedRef.current) {
      setIsPaused(false);
      setIsManuallyPaused(false);
      lastCheckRef.current = Date.now();
    }
  }, []);

  const reset = useCallback(() => {
    setTimeElapsed(0);
    setIsPaused(false);
    setIsCompleted(false);
    setIsRunning(false);
    setIsManuallyPaused(false);
    lastCheckRef.current = Date.now();
    clearTimers();
  }, []);

  // ⏱ Main timer loop
  useEffect(() => {
    syncRefs();

    if (!isRunning) {
      clearTimers();
      return;
    }

    intervalRef.current = setInterval(() => {
      const timeSinceLastActivity = getTimeSinceLastActivity();

      // Only check for inactivity if the timer is running and not manually paused
      if (
        !isPausedRef.current &&
        !isCompletedRef.current &&
        isRunningRef.current
      ) {
        // Check for inactivity and pause if needed (but only if not manually paused)
        if (
          !isManuallyPausedRef.current &&
          timeSinceLastActivity >= inactivityThreshold
        ) {
          setIsPaused(true);
          return;
        }

        setTimeElapsed((prev) => {
          const newTime = prev + 1;
          if (totalTime > 0 && newTime >= totalTime) {
            // Use setTimeout to avoid calling handleCompletion inside setInterval
            setTimeout(() => handleCompletion(), 0);
            return totalTime;
          }
          return newTime;
        });
      }
    }, tickInterval);

    return clearTimers;
  }, [
    isRunning,
    isPaused,
    isCompleted,
    totalTime,
    tickInterval,
    getTimeSinceLastActivity,
    inactivityThreshold,
    handleCompletion,
    syncRefs,
  ]);

  // 🧹 Cleanup on unmount
  useEffect(() => clearTimers, []);

  const resetActivity = useCallback(() => {
    lastCheckRef.current = Date.now();
  }, []);

  const progress = totalTime > 0 ? Math.min(timeElapsed / totalTime, 1) : 0;

  return {
    timeElapsed,
    isPaused,
    isCompleted,
    progress,
    start,
    pause,
    resume,
    reset,
    resetActivity,
  };
};
